import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Users as UsersIcon, Plus, Check, X, ChevronDown,
  Crown, Inbox, Play, Ban, Trash, Pause, Search as SearchIcon, CheckCircle2, Clock,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/components/AuthProvider";

// Keep in sync with server's ASSIGNMENT_STATUSES
export const ASSIGNMENT_STATUSES = [
  { k: "pending",     label: "Pendiente",   icon: Clock,        cls: "bg-slate-500/15 text-slate-600 dark:text-slate-300" },
  { k: "received",    label: "Recibido",    icon: Inbox,        cls: "bg-sky-500/15 text-sky-600 dark:text-sky-300" },
  { k: "in_progress", label: "En proceso",  icon: Play,         cls: "bg-blue-500/15 text-blue-600 dark:text-blue-300" },
  { k: "blocked",     label: "Bloqueado",   icon: Ban,          cls: "bg-rose-500/15 text-rose-600 dark:text-rose-300" },
  { k: "postponed",   label: "Postergado",  icon: Pause,        cls: "bg-amber-500/15 text-amber-700 dark:text-amber-300" },
  { k: "review",      label: "En revisión", icon: SearchIcon,   cls: "bg-purple-500/15 text-purple-600 dark:text-purple-300" },
  { k: "completed",   label: "Completado",  icon: CheckCircle2, cls: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300" },
  { k: "denied",      label: "Denegado",    icon: X,            cls: "bg-rose-500/15 text-rose-700 dark:text-rose-300" },
  { k: "discarded",   label: "Descartado",  icon: Trash,        cls: "bg-slate-500/20 text-slate-700 dark:text-slate-300" },
];

function statusMeta(k) {
  return ASSIGNMENT_STATUSES.find((s) => s.k === k) || ASSIGNMENT_STATUSES[0];
}

function initials(name = "") {
  return name.split(/\s+/).map(p => p[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "?";
}

export default function CardAssignmentsSection({ cardId, boardId, card, qc }) {
  const { user: me } = useAuth();
  const fallbackClient = useQueryClient();
  const client = qc || fallbackClient;

  // Fetch board members (creator + accepted members)
  const { data: boardDetails } = useQuery({
    queryKey: ["board-details", boardId],
    queryFn: async () => (await api.get(`/boards/${boardId}/details`)).data,
    staleTime: 60_000,
  });

  const memberDirectory = useMemo(() => {
    const list = [];
    if (boardDetails?.author) list.push({ ...boardDetails.author, is_creator: true });
    for (const m of boardDetails?.members || []) {
      if (m.status !== "active") continue;
      if (list.find((x) => x.user_id === m.user_id)) continue;
      list.push({ ...m, is_creator: false });
    }
    return list;
  }, [boardDetails]);

  const currentAssignments = card?.assignments || [];
  const assignedIds = currentAssignments.map((a) => a.user_id);
  const canManage = boardDetails?.is_owner || card?.created_by === me?.user_id;
  const myAssignment = currentAssignments.find((a) => a.user_id === me?.user_id);

  const setAssignees = useMutation({
    mutationFn: async (user_ids) => (await api.post(`/cards/${cardId}/assignments`, { user_ids })).data,
    onSuccess: () => {
      toast.success("Asignados actualizados");
      client.invalidateQueries({ queryKey: ["card", cardId] });
      client.invalidateQueries({ queryKey: ["board-full", boardId] });
      client.invalidateQueries({ queryKey: ["board-details", boardId] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo actualizar"),
  });

  const setMyStatus = useMutation({
    mutationFn: async (status) =>
      (await api.patch(`/cards/${cardId}/assignments/${me.user_id}/status`, { status })).data,
    onSuccess: () => {
      toast.success("Estado actualizado");
      client.invalidateQueries({ queryKey: ["card", cardId] });
      client.invalidateQueries({ queryKey: ["notifications-count"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo actualizar"),
  });

  const toggle = (uid) => {
    const set = new Set(assignedIds);
    if (set.has(uid)) set.delete(uid);
    else set.add(uid);
    setAssignees.mutate(Array.from(set));
  };

  const removeAll = (uid) => {
    setAssignees.mutate(assignedIds.filter((x) => x !== uid));
  };

  return (
    <div data-testid="card-assignments-section" className="rounded-lg bg-white/40 dark:bg-slate-900/40 p-3 border border-white/30 dark:border-white/5 space-y-3">
      <div className="flex items-center gap-2 text-sm font-medium">
        <UsersIcon className="h-4 w-4" />
        Asignados
        <span className="ml-auto text-[11px] text-muted-foreground">{currentAssignments.length}</span>
      </div>

      {/* My status selector: only visible when the current user is one of the assignees */}
      {myAssignment && (
        <div className="rounded-md bg-blue-500/10 border border-blue-500/20 p-2.5" data-testid="card-my-assignment">
          <p className="text-[10px] uppercase tracking-wider text-blue-700 dark:text-blue-300 font-semibold mb-1.5">
            Tu estado en esta tarea
          </p>
          <MyStatusPicker current={myAssignment.status} onChange={(s) => setMyStatus.mutate(s)} />
        </div>
      )}

      {/* Assignees list */}
      <ul className="space-y-1.5" data-testid="card-assignees-list">
        {currentAssignments.map((a) => {
          const info = memberDirectory.find((m) => m.user_id === a.user_id) || {};
          const s = statusMeta(a.status);
          const S = s.icon;
          return (
            <li
              key={a.user_id}
              className="flex items-center gap-2.5 rounded-lg p-1.5 hover:bg-white/50 dark:hover:bg-white/5 transition"
              data-testid={`card-assignee-${a.user_id}`}
            >
              <Avatar className="h-7 w-7">
                <AvatarImage src={info.picture} alt={info.name} />
                <AvatarFallback className="text-[10px] bg-blue-500/20">{initials(info.name || "?")}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate flex items-center gap-1">
                  {info.name || "Usuario"}
                  {info.is_creator && <Crown className="h-3 w-3 text-amber-500" title="Creador del tablero" />}
                  {a.user_id === me?.user_id && (
                    <span className="text-[10px] uppercase tracking-wider text-blue-600 dark:text-blue-300">(tú)</span>
                  )}
                </p>
                <span className={cn("inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded", s.cls)}
                      data-testid={`card-assignee-status-${a.user_id}`}>
                  <S className="h-2.5 w-2.5" /> {s.label}
                </span>
              </div>
              {canManage && (
                <button
                  onClick={() => removeAll(a.user_id)}
                  data-testid={`card-unassign-${a.user_id}`}
                  className="h-6 w-6 grid place-items-center rounded-md text-muted-foreground hover:bg-rose-500/10 hover:text-rose-600 transition"
                  aria-label="Quitar asignación"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          );
        })}
        {currentAssignments.length === 0 && (
          <li className="text-xs text-muted-foreground italic px-1">Sin asignados</li>
        )}
      </ul>

      {/* Add-assignee popover */}
      {canManage && memberDirectory.length > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              data-testid="card-add-assignee"
              className="w-full text-left px-2.5 py-1.5 rounded-lg text-xs text-muted-foreground hover:bg-white/50 dark:hover:bg-white/5 inline-flex items-center gap-1.5 transition"
            >
              <Plus className="h-3.5 w-3.5" /> Añadir asignado
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-64 glass-panel rounded-2xl p-2" data-testid="card-add-assignee-popover">
            <div className="space-y-1 max-h-72 overflow-y-auto kanban-scroll">
              {memberDirectory.map((m) => {
                const on = assignedIds.includes(m.user_id);
                return (
                  <button
                    key={m.user_id}
                    onClick={() => toggle(m.user_id)}
                    data-testid={`card-toggle-assignee-${m.user_id}`}
                    className={cn(
                      "w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm hover:bg-white/40 dark:hover:bg-white/10 transition",
                      on && "bg-blue-500/10"
                    )}
                  >
                    <Avatar className="h-6 w-6">
                      <AvatarImage src={m.picture} alt={m.name} />
                      <AvatarFallback className="text-[9px] bg-blue-500/20">{initials(m.name || "?")}</AvatarFallback>
                    </Avatar>
                    <span className="flex-1 text-left truncate">{m.name}{m.is_creator && " · Creador"}</span>
                    {on && <Check className="h-3.5 w-3.5 text-blue-600" />}
                  </button>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}

function MyStatusPicker({ current, onChange }) {
  const s = statusMeta(current);
  const S = s.icon;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid="card-my-status-trigger"
          className={cn(
            "w-full inline-flex items-center gap-2 px-3 h-9 rounded-lg text-sm font-medium transition border border-white/40 dark:border-white/10 bg-white/70 dark:bg-slate-900/40 hover:bg-white/90"
          )}
        >
          <span className={cn("inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded", s.cls)}>
            <S className="h-3 w-3" /> {s.label}
          </span>
          <ChevronDown className="h-4 w-4 ml-auto text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 glass-panel rounded-xl p-1.5" data-testid="card-my-status-popover">
        <div className="space-y-0.5">
          {ASSIGNMENT_STATUSES.filter((x) => x.k !== "pending").map((opt) => {
            const O = opt.icon;
            const active = current === opt.k;
            return (
              <button
                key={opt.k}
                onClick={() => onChange(opt.k)}
                data-testid={`card-my-status-${opt.k}`}
                className={cn(
                  "w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm hover:bg-white/50 dark:hover:bg-white/10 transition",
                  active && "bg-blue-500/10 font-medium"
                )}
              >
                <span className={cn("inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded", opt.cls)}>
                  <O className="h-3 w-3" />
                </span>
                <span className="flex-1 text-left">{opt.label}</span>
                {active && <Check className="h-3.5 w-3.5 text-blue-600" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
